import { LitElement, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import { styles } from "./stream-stats.css";

interface StreamStats {
  id: string;
  name: string;
  clients: number;
  publish: {
    active: boolean;
  };
  kbps: {
    recv_30s: number;
    send_30s: number;
  };
  recv_bytes: number;
  send_bytes: number;
}

interface SRSStreamsResponse {
  code: number;
  server: string;
  streams?: StreamStats[];
}

@customElement("stream-stats")
export class StreamStatsComponent extends LitElement {
  static override styles = styles;

  @property({ type: String })
  eventId?: string;

  @property({ type: String })
  scheduledDateTime?: string;

  @state()
  private streamStats: StreamStats | null = null;

  @state()
  private isLoadingStats = false;

  @state()
  private statsError = "";

  private statsPollingInterval: number | null = null;

  override connectedCallback() {
    super.connectedCallback();
    if (this.shouldShowStreamStats()) {
      this.startStatsPolling();
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.stopStatsPolling();
  }

  override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);

    if (changedProperties.has("eventId") || changedProperties.has("scheduledDateTime")) {
      this.stopStatsPolling();
      if (this.shouldShowStreamStats()) {
        this.startStatsPolling();
      }
    }
  }

  private shouldShowStreamStats(): boolean {
    if (!this.eventId || !this.scheduledDateTime) return false;

    const now = Date.now();
    const scheduledTime = new Date(this.scheduledDateTime).getTime();
    return now >= scheduledTime;
  }

  private async fetchStreamStats(): Promise<void> {
    if (!this.eventId) return;

    try {
      const response = await fetch("https://stream.worldofwow.dev:1990/api/v1/streams");
      const data = (await response.json()) as SRSStreamsResponse;

      const stream = data.streams?.find((s) => s.name === this.eventId);
      this.streamStats = stream || null;
      this.statsError = stream ? "" : "Stream not found";
    } catch (error) {
      this.statsError = "Failed to fetch stream statistics";
      console.error("Error fetching stream stats:", error);
    }
  }

  private startStatsPolling(): void {
    if (this.statsPollingInterval !== null) return;

    this.isLoadingStats = true;
    void this.fetchStreamStats().finally(() => {
      this.isLoadingStats = false;
    });

    this.statsPollingInterval = window.setInterval(() => {
      void this.fetchStreamStats();
    }, 1000);
  }

  private stopStatsPolling(): void {
    if (this.statsPollingInterval !== null) {
      clearInterval(this.statsPollingInterval);
      this.statsPollingInterval = null;
    }
  }

  private formatBandwidth(kbps: number): string {
    const mbps = kbps / 1000;
    return `${mbps.toFixed(2)} Mbps`;
  }

  private formatBytes(bytes: number): string {
    const mb = bytes / (1024 * 1024);
    if (mb >= 1000) {
      const gb = mb / 1024;
      return `${gb.toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  }

  override render() {
    if (!this.shouldShowStreamStats()) {
      return nothing;
    }

    if (this.isLoadingStats) {
      return html`
        <div class="stream-stats-section">
          <div class="stats-header">Live Stream Statistics</div>
          <div class="loading-state">Loading stream statistics...</div>
        </div>
      `;
    }

    if (this.statsError) {
      return html`
        <div class="stream-stats-section">
          <div class="stats-header">Live Stream Statistics</div>
          <div class="error-message">${this.statsError}</div>
        </div>
      `;
    }

    if (!this.streamStats) {
      return nothing;
    }

    const status = this.streamStats.publish?.active ? "Live" : "Offline";
    const statusClass = this.streamStats.publish?.active ? "status-live" : "status-offline";

    return html`
      <div class="stream-stats-section">
        <div class="stats-header">Live Stream Statistics</div>
        <div class="stats-grid">
          <div class="stat-item">
            <div class="stat-label">Stream ID</div>
            <div class="stat-value">${this.streamStats.id}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">Status</div>
            <div class="stat-value ${statusClass}">${status}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">Clients</div>
            <div class="stat-value">${this.streamStats.clients}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">Inbound</div>
            <div class="stat-value">${this.formatBandwidth(this.streamStats.kbps?.recv_30s || 0)}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">Outbound</div>
            <div class="stat-value">${this.formatBandwidth(this.streamStats.kbps?.send_30s || 0)}</div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "stream-stats": StreamStatsComponent;
  }
}
